import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ChevronLeft, Send, Shield, CheckCircle, AlertCircle, Scale } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { calcularClassificacaoLegal, getCorGrau, getDescricaoNivelCuidado, type GrauClassificacao } from '@/lib/classificacaoLegal'

const GRAU_LABELS: Record<GrauClassificacao, string> = {
  'I': 'Grau I — Independente',
  'I-com-apoio': 'Grau I — Independente com Apoio',
  'II': 'Grau II — Cadeira de Rodas',
  'III': 'Grau III — Acamado',
};

interface Etapa10Props {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
  onSubmit: () => void
  saving: boolean
  submitting: boolean
}

export function Etapa10Finalizacao({ formData, onChange, onPrev, onSubmit, submitting }: Etapa10Props) {
  const isFormValid = formData.lgpd_accept && formData.declaration_accept && formData.signature

  // Função de validação com feedback
  const validateAndSubmit = () => {
    const errors: string[] = []

    if (!formData.lgpd_accept) errors.push('Aceite dos termos LGPD')
    if (!formData.declaration_accept) errors.push('Declaração de veracidade')
    if (!formData.signature) errors.push('Assinatura do responsável')

    if (errors.length > 0) {
      alert(`Por favor, preencha os campos obrigatórios:\n\n${errors.join('\n')}`)
      return
    }

    onSubmit()
  }
  
  // Calcular classificação legal
  const classificacao = calcularClassificacaoLegal(formData)
  const corGrau = getCorGrau(classificacao.grau)

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Não informado'
    try {
      const date = new Date(dateString)
      return format(date, 'dd/MM/yyyy', { locale: ptBR })
    } catch {
      return 'Data inválida'
    }
  }

  const calculateAge = (dateString: string) => {
    if (!dateString) return 'Idade desconhecida'
    try {
      const birthDate = new Date(dateString)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }
      return age
    } catch {
      return 'Idade inválida'
    }
  }

  const pulseClass = (active: boolean) => active ? '' : 'animate-pulse-required';

  return (
    <>
      <style>{`
        @keyframes pulse-required {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); border-color: rgba(239, 68, 68, 0.8); }
          50% { box-shadow: 0 0 8px 3px rgba(239, 68, 68, 0.3); border-color: rgba(239, 68, 68, 0.4); }
        }
        .animate-pulse-required {
          animation: pulse-required 1.5s ease-in-out infinite;
          border-radius: 0.5rem;
          border: 2px solid rgba(239, 68, 68, 0.8);
          padding: 0.25rem;
        }
        input.animate-pulse-required {
          border-radius: 0.375rem;
        }
      `}</style>
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Finalização e Envio
          </CardTitle>
          <p className="text-sm text-gray-600">
            Revise as informações e finalize o envio da anamnese.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Card de Classificação Legal - RDC ANVISA 283/2005 */}
          <Card className={`border-2 ${corGrau.split(' ')[1]} ${corGrau.split(' ')[2]}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Classificação Legal - RDC ANVISA 283/2005
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className={`text-2xl font-bold ${corGrau.split(' ')[0]}`}>
                  {GRAU_LABELS[classificacao.grau]}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Pontuação: {classificacao.pontuacao}/100
                </div>
                <div className="text-sm font-medium mt-2">
                  {classificacao.descricao}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Nível de Cuidado: {getDescricaoNivelCuidado(classificacao.nivelCuidado)}
                </div>
              </div>

              {/* Aviso de Estimativa */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    <strong>Importante:</strong> Este cálculo é automático e representa uma estimativa gerada a partir dos dados fornecidos na anamnese de triagem. 
                    A classificação definitiva precisa ser confirmada por profissional habilitado e pode ser alterada segundo o entendimento técnico desse profissional.
                  </span>
                </p>
              </div>
              
              {/* Critérios Aplicados */}
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-700 mb-2">Critérios Avaliados:</p>
                <div className="flex flex-wrap gap-1">
                  {classificacao.criteriosAplicados.map((criterio, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {criterio}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resumo Completo da Anamnese */}
          <div className="space-y-6">
            <h4 className="font-semibold text-lg">Resumo Completo da Anamnese</h4>
            
            {/* Etapa 1 - Dados do Idoso(a) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">1</div>
                  Dados do Idoso(a)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500">Nome Completo</p>
                    <p className="text-sm">{formData.nome_completo || 'Não informado'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Data de Nascimento</p>
                    <p className="text-sm">{formatDate(formData.data_nascimento)} ({calculateAge(formData.data_nascimento)} anos)</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">CPF</p>
                    <p className="text-sm">{formData.cpf || 'Não informado'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">RG</p>
                    <p className="text-sm">{formData.rg || 'Não informado'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Etapa 2 - Responsável */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">2</div>
                  Responsável
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500">Nome do Responsável</p>
                    <p className="text-sm">{formData.responsavel?.nome || 'Não informado'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Parentesco</p>
                    <p className="text-sm">{formData.responsavel?.parentesco || 'Não informado'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">WhatsApp</p>
                    <p className="text-sm">{formData.responsavel?.whatsapp || 'Não informado'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">E-mail</p>
                    <p className="text-sm">{formData.responsavel?.email || 'Não informado'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Responsável Financeiro?</p>
                    <p className="text-sm">{formData.responsavel?.responsavel_financeiro ? 'Sim' : 'Não'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Existe Curatela?</p>
                    <p className="text-sm">{formData.responsavel?.existe_curatela ? 'Sim' : 'Não'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Etapa 3 - Histórico Clínico */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">3</div>
                  Histórico Clínico
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Condições de Saúde ({formData.comorbidades?.filter((c: any) => c.possui).length || 0})</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {formData.comorbidades?.filter((c: any) => c && c.possui).map((comorbidade: any, index: number) => (
                      <div key={index} className="bg-gray-50 rounded p-2">
                        <p className="text-sm font-medium capitalize">
                          {(comorbidade?.condicao || '').replace(/_/g, ' ')}
                        </p>
                        {comorbidade?.detalhes && (
                          <p className="text-xs text-gray-600 mt-1">{comorbidade.detalhes}</p>
                        )}
                      </div>
                    ))}
                    {(!formData.comorbidades?.filter((c: any) => c.possui).length) && (
                      <p className="text-sm text-gray-500">Nenhuma condição informada</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Etapa 4 - Medicamentos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">4</div>
                  Medicamentos ({formData.medicamentos?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {formData.medicamentos?.length > 0 ? (
                  <div className="space-y-3">
                    {formData.medicamentos.map((med: any, index: number) => {
                      if (!med) return null;
                      return (
                        <div key={index} className="bg-gray-50 rounded p-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <p className="text-xs font-medium text-gray-500">Medicamento</p>
                              <p className="text-sm font-medium">{med.name || 'Não informado'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Horários</p>
                              <p className="text-sm">{(med.schedules || []).join(', ') || 'Não informado'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Forma</p>
                              <p className="text-sm">{med.medicationForm || 'Não informado'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Quantidade/dose</p>
                              <p className="text-sm">{med.quantityPerDose ? `${med.quantityPerDose} ${med.measurementUnit || ''}` : 'Não informado'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Recorrência</p>
                              <p className="text-sm">{med.recurrence === 'daily' ? 'Diária' : med.recurrence === 'range' ? 'Período específico' : 'Apenas uma vez'}</p>
                            </div>
                          </div>
                          {med.instructions && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-gray-500">Instruções</p>
                              <p className="text-sm">{med.instructions}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Nenhum medicamento cadastrado</p>
                )}
              </CardContent>
            </Card>

            {/* Etapa 5 - Capacidade Funcional */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">5</div>
                  Capacidade Funcional ({formData.funcionalidade?.length || 0} atividades)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {formData.funcionalidade?.map((func: any, index: number) => {
                    if (!func) return null;
                    const labels: Record<string, string> = {
                      alimentacao: 'Alimentação',
                      banho: 'Banho/Higiene',
                      vestir: 'Vestir-se',
                      higiene_intima: 'Higiene Íntima',
                      caminhar: 'Caminhar',
                      levantar: 'Levantar',
                      sentar: 'Sentar',
                      banheiro: 'Usar o Banheiro',
                      continencia_urinaria: 'Continência Urinária',
                      continencia_fecal: 'Continência Fecal',
                      medicamentos: 'Administrar Medicamentos'
                    };
                    return (
                      <div key={index} className="bg-gray-50 rounded p-3">
                        <p className="text-sm font-medium">{labels[func.atividade] || func.atividade || 'Atividade'}</p>
                        <p className="text-xs text-gray-600 mt-1">Nível: {(func.nivel_dependencia || '').replace(/_/g, ' ')}</p>
                        {func.observacoes && (
                          <p className="text-xs text-gray-600 mt-1">Obs: {func.observacoes}</p>
                        )}
                      </div>
                    );
                  })}
                  {(!formData.funcionalidade?.length) && (
                    <p className="text-sm text-gray-500">Nenhuma atividade avaliada</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Etapa 6 - Cognição e Comportamento */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">6</div>
                  Cognição e Comportamento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(formData.cognicao || {}).map(([key, value]) => {
                    if (typeof value !== 'boolean') return null;
                    return (
                      <div key={key} className={`p-2 rounded ${value ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                        <p className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-gray-600">{value ? 'Sim' : 'Não'}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Etapa 7 - Mobilidade e Risco */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">7</div>
                  Mobilidade e Risco
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(formData.mobilidade || {}).map(([key, value]) => {
                    if (typeof value !== 'boolean') return null;
                    return (
                      <div key={key} className={`p-2 rounded ${value ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                        <p className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-gray-600">{value ? 'Sim' : 'Não'}</p>
                      </div>
                    );
                  })}
                </div>
                {formData.mobilidade?.observacoes && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-500">Observações Adicionais</p>
                    <p className="text-sm">{formData.mobilidade.observacoes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Etapa 8 - Nutrição */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">8</div>
                  Nutrição
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500">Peso</p>
                    <p className="text-sm">{formData.nutricao?.peso || 'Não informado'} kg</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Altura</p>
                    <p className="text-sm">{formData.nutricao?.altura || 'Não informado'} cm</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Dieta</p>
                    <p className="text-sm">{formData.nutricao?.dieta || 'Não informada'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Engasgos</p>
                    <p className="text-sm">{formData.nutricao?.engasgos ? 'Sim' : 'Não'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Sonda</p>
                    <p className="text-sm">{formData.nutricao?.sonda ? 'Sim' : 'Não'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Perda de Peso</p>
                    <p className="text-sm">{formData.nutricao?.perda_peso ? 'Sim' : 'Não'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Etapa 9 - Capacidades Sensoriais */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">9</div>
                  Capacidades Sensoriais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(formData.sensorial || {}).map(([key, value]) => {
                    if (typeof value !== 'boolean') return null;
                    return (
                      <div key={key} className={`p-2 rounded ${value ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                        <p className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-gray-600">{value ? 'Sim' : 'Não'}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fundamentação Legal */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Fundamentação Legal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs space-y-1">
                {classificacao.fundamentacaoLegal.map((fundamento, index) => (
                  <p key={index} className="text-gray-600">• {fundamento}</p>
                ))}
              </div>
              
              {/* Detalhes da Pontuação */}
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs font-medium text-gray-700 mb-2">Detalhes da Pontuação:</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-medium">Funcional</div>
                    <div className="text-gray-600">{classificacao.detalhes.funcional} pts</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">Cognitivo</div>
                    <div className="text-gray-600">{classificacao.detalhes.cognitivo} pts</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">Clínico</div>
                    <div className="text-gray-600">{classificacao.detalhes.clinico} pts</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">Sensorial</div>
                    <div className="text-gray-600">{classificacao.detalhes.sensorial} pts</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">Nutricional</div>
                    <div className="text-gray-600">{classificacao.detalhes.nutricional} pts</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Termos LGPD */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="h-5 w-5" />
                Lei Geral de Proteção de Dados (LGPD)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Ao enviar esta anamnese, você concorda com o tratamento de seus dados pessoais 
                  de acordo com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
                </AlertDescription>
              </Alert>

              <div className="space-y-2 text-sm text-gray-600">
                <p>• Seus dados serão utilizados exclusivamente para fins de triagem e admissão</p>
                <p>• As informações serão armazenadas com segurança e confidencialidade</p>
                <p>• Você terá direito de acesso, correção e exclusão de seus dados</p>
                <p>• Seus dados não serão compartilhados com terceiros sem seu consentimento</p>
              </div>

              <div className={`flex items-center space-x-2 ${pulseClass(formData.lgpd_accept || false)}`}>
                <Checkbox
                  id="lgpd_accept"
                  checked={formData.lgpd_accept || false}
                  onCheckedChange={(checked) => onChange('lgpd_accept', checked as boolean)}
                  className="!rounded-full data-[state=checked]:!bg-green-600 data-[state=checked]:!border-green-600"
                />
                <Label htmlFor="lgpd_accept" className="text-sm">
                  Li e concordo com os termos de proteção de dados (LGPD)
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Declaração de Veracidade */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckCircle className="h-5 w-5" />
                Declaração de Veracidade
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Declaro que todas as informações fornecidas nesta anamnese são verdadeiras 
                  e completas, assumindo a responsabilidade civil e criminal por eventuais omissões 
                  ou informações falsas.
                </AlertDescription>
              </Alert>

              <div className={`flex items-center space-x-2 ${pulseClass(formData.declaration_accept || false)}`}>
                <Checkbox
                  id="declaration_accept"
                  checked={formData.declaration_accept || false}
                  onCheckedChange={(checked) => onChange('declaration_accept', checked as boolean)}
                  className="!rounded-full data-[state=checked]:!bg-green-600 data-[state=checked]:!border-green-600"
                />
                <Label htmlFor="declaration_accept" className="text-sm font-medium">
                  Declaro que todas as informações são verdadeiras
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Assinatura Digital */}
          <div className="space-y-4">
            <Label htmlFor="signature">Assinatura (nome completo)</Label>
            <Input
              id="signature"
              value={formData.signature || ''}
              onChange={(e) => onChange('signature', e.target.value)}
              placeholder="Digite seu nome completo como assinatura digital"
              className={`mt-1 ${pulseClass(!!formData.signature)}`}
            />
            <p className="text-xs text-gray-500">
              Ao digitar seu nome, você está assinando digitalmente esta anamnese.
            </p>
          </div>

          {/* Observações Finais */}
          <div>
            <Label htmlFor="observacoes_finais">Observações Finais (opcional)</Label>
            <Textarea
              id="observacoes_finais"
              value={formData.observacoes_finais || ''}
              onChange={(e) => onChange('observacoes_finais', e.target.value)}
              placeholder="Alguma informação adicional que você ache importante?"
              className="mt-1"
              rows={3}
            />
          </div>

          {/* Alerta Final */}
          <Alert className="border-blue-200 bg-blue-50">
            <CheckCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Atenção:</strong> Após o envio, esta anamnese não poderá mais ser editada. 
              Nossa equipe entrará em contato em até 48 horas úteis para os próximos passos.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Botões de Navegação */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev} disabled={submitting}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        
        <Button
          onClick={validateAndSubmit}
          disabled={submitting}
          className="bg-green-600 hover:bg-green-700"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Enviando...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Enviar Anamnese
            </>
          )}
        </Button>
      </div>

      {/* Status de Validação */}
      <div className="flex flex-wrap gap-2">
        <Badge variant={formData.lgpd_accept ? "default" : "secondary"}>
          {formData.lgpd_accept ? "✓ LGPD Aceito" : "✗ LGPD Pendente"}
        </Badge>
        <Badge variant={formData.declaration_accept ? "default" : "secondary"}>
          {formData.declaration_accept ? "✓ Declaração Aceita" : "✗ Declaração Pendente"}
        </Badge>
        <Badge variant={formData.signature ? "default" : "secondary"}>
          {formData.signature ? "✓ Assinado" : "✗ Assinatura Pendente"}
        </Badge>
      </div>
    </div>
    </>
  )
}
